// Config-driven particle avatar. Samples a portrait into a luminous dot cloud
// (per-region colors/density, fidelity, facial deformations, per-state motion).
// Falls back to a parametric silhouette when no photo is set.

import { useEffect, useRef } from 'react';
import {
  type AvatarConfig, type AvatarState, type Particle, type ParticleField,
  DEFAULT_AVATAR_CONFIG, DEFAULT_FACE_CONFIG, DEFAULT_FACE_IMAGE,
  sampleImageField, loadAuthedImage, useEffectiveAvatar,
} from '../lib/avatar.js';
import { speechLevel } from '../lib/audioBus.js';
import { EXPRESSIONS, type Expression } from '../lib/expression.js';
import { usePrefs } from '../lib/prefs.js';
import { createGLRenderer, type GLRenderer } from '../lib/glPoints.js';

// Parse 'rgb(r,g,b)' / 'rgba(...)' / '#hex' → [r,g,b] 0..255, or null.
function parseRgb(s: string | null | undefined): [number, number, number] | null {
  if (!s) return null;
  const m = /rgba?\(([^)]+)\)/i.exec(s);
  if (m) { const p = m[1]!.split(',').map((x) => parseFloat(x)); return [p[0]!, p[1]!, p[2]!]; }
  const h = /^#?([0-9a-f]{6})$/i.exec(s.trim());
  if (h) { const n = parseInt(h[1]!, 16); return [(n >> 16) & 255, (n >> 8) & 255, n & 255]; }
  return null;
}

// Load a same-origin static image (no auth/API prefix) for the built-in default.
function loadPlainImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(`image load ${url}`));
    img.src = url;
  });
}

export type DotHeadMode = AvatarState;

interface Props {
  mode?: DotHeadMode;
  size?: number;
  /** Explicit config (live-preview in the Avatar Lab). If omitted, fetched via useAvatar(). */
  config?: AvatarConfig;
  /** Explicit photo URL. If omitted, the active photo from useAvatar() is used. */
  photoUrl?: string | null;
  /** Bump to force a resample even if photoUrl/config-key didn't change. */
  resampleKey?: string | number;
  /** Emotional cast layered on top of the config (smile / eyes / brow). */
  expression?: Expression;
}

function easeOutCubic(t: number) { return 1 - Math.pow(1 - t, 3); }

interface MotionParams { driftAmp: number; listen: number; think: number; speak: number; alphaMul: number; focusMul: number; timeMul: number }

function motionParams(state: AvatarState, T: number, cfg: AvatarConfig): MotionParams {
  const sp = cfg.stateSpeeds, fq = cfg.stateFreqs;
  const timeMul =
    state === 'idle' ? fq.idle : state === 'listening' ? fq.listening
    : state === 'thinking' ? fq.thinking : state === 'speaking' ? fq.speaking : 1;
  if (state === 'idle') return { driftAmp: 4.2 * sp.idle, listen: 0, think: 0, speak: 0, alphaMul: 0.85, focusMul: 0, timeMul };
  if (state === 'listening') return { driftAmp: 3.0 * sp.listening, listen: sp.listening, think: 0, speak: 0, alphaMul: 1.0, focusMul: 0.25, timeMul };
  if (state === 'thinking') return { driftAmp: 2.4 * sp.thinking, listen: 0, think: 0.85 * sp.thinking, speak: 0, alphaMul: 0.9 + Math.sin(T * 6) * 0.1, focusMul: 0.15, timeMul };
  if (state === 'speaking') return { driftAmp: 0.45 * sp.speaking, listen: 0, think: 0, speak: sp.speaking, alphaMul: 1.05, focusMul: 1.35, timeMul };
  if (state === 'forming') return { driftAmp: 0.5, listen: 0, think: 0, speak: 0, alphaMul: 1.0, focusMul: 1.1, timeMul: 1 };
  return { driftAmp: 1, listen: 0, think: 0, speak: 0, alphaMul: 1, focusMul: 0, timeMul: 1 };
}

// Parametric fallback: an egg-shaped silhouette so the avatar still reads as a
// head when no photo has been uploaded.
function parametricField(sampleW: number): ParticleField {
  const particles: Particle[] = [];
  const count = Math.min(2400, Math.max(700, sampleW * 6));
  for (let i = 0; i < count; i++) {
    const t = Math.random() * Math.PI * 2;
    const r = Math.sqrt(Math.random());
    const tx = 0.5 + Math.cos(t) * 0.32 * r;
    const ty = 0.46 + Math.sin(t) * 0.40 * r;
    const oAng = Math.random() * Math.PI * 2;
    const oRad = 1.6 + Math.random() * 2.4;
    particles.push({
      tx, ty, lum: 0.4 + Math.random() * 0.5, color: 'rgb(232,185,122)',
      phase: Math.random() * Math.PI * 2, speed: 0.3 + Math.random() * 0.8,
      dr: 0.8 + Math.random() * 1.6, ox: 0.5 + Math.cos(oAng) * oRad, oy: 0.5 + Math.sin(oAng) * oRad,
      formDelay: Math.random() * 0.4, mouthWeight: 0, featureWeight: 0,
      region: 'skin', isTeeth: false, halo: false, rng: Math.random(), cx: 0, cy: 0,
    });
  }
  return { particles, aspect: 0.82 };
}

function densityKeep(region: string, density: AvatarConfig['regionDensity']): number {
  const FEATURE: Record<string, boolean> = { eyes: true, brows: true, nose: true, mouth: true };
  const k = (density as unknown as Record<string, number>)[region];
  if (typeof k !== 'number') return 1;
  return FEATURE[region] ? k * 0.25 : k;
}

// Adaptive point budget — stronger devices render a denser, higher-resolution
// cloud; weaker / mobile devices render fewer dots so the loop stays at 60fps.
function deviceBudget(): number {
  if (typeof navigator === 'undefined') return 1;
  const cores = navigator.hardwareConcurrency || 4;
  const mobile = /Mobi|Android|iPhone|iPad/i.test(navigator.userAgent || '');
  let m = cores >= 8 ? 1.3 : cores >= 4 ? 1.0 : 0.7;
  if (mobile) m *= 0.8;
  return Math.max(0.5, Math.min(1.4, m));
}

// Layering order: halo behind, skin/hair mid, facial features in front, so the
// eyes/mouth read crisply on top of the cloud.
function depthKey(p: Particle): number {
  if (p.halo) return 0;
  return 1 + p.featureWeight; // higher featureWeight → drawn later (front)
}

export function DotHead({ mode = 'idle', size = 300, config, photoUrl, resampleKey, expression = 'neutral' }: Props) {
  const eff = useEffectiveAvatar(); // org-aware: a colleague gets their org's avatar
  const resolvedPhoto = photoUrl !== undefined ? photoUrl : (eff.photoUrl ?? null);
  // The "no photo" avatar is a LOCKED default: always the golden crystal face,
  // never configurable. A real photo uses the (configurable) portrait settings.
  const cfg = resolvedPhoto
    ? (config ?? eff.config ?? DEFAULT_AVATAR_CONFIG)
    : DEFAULT_FACE_CONFIG;

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const cfgRef = useRef(cfg); cfgRef.current = cfg;
  const modeRef = useRef<AvatarState>(mode); modeRef.current = mode;
  const exprRef = useRef<Expression>(expression); exprRef.current = expression;
  const fieldRef = useRef<ParticleField | null>(null);
  const renderRef = useRef<Particle[] | null>(null);
  const formingRef = useRef(0);
  const budgetRef = useRef(deviceBudget());
  // GPU path: precomputed numeric colors + reusable per-frame attribute arrays.
  const colorsRef = useRef<Float32Array | null>(null);
  const glArraysRef = useRef<{ pos: Float32Array; size: Float32Array; col: Float32Array } | null>(null);
  const [prefs] = usePrefs();
  const renderer = prefs.avatarRenderer;

  // Build/resample the base field when the photo or build-affecting config changes.
  const buildKey = JSON.stringify([
    resolvedPhoto, cfg.sampleW, cfg.regionColors, cfg.colorOpts, cfg.regionDensity, cfg.faceRegions, cfg.photoColorMix, cfg.cropToHead, resampleKey,
  ]);

  useEffect(() => {
    let cancelled = false;
    async function build() {
      let field: ParticleField;
      if (resolvedPhoto) {
        try {
          const img = await loadAuthedImage(resolvedPhoto);
          if (cancelled) return;
          field = sampleImageField(img, cfgRef.current);
        } catch {
          field = parametricField(cfgRef.current.sampleW);
        }
      } else {
        // No photo → sample the built-in golden crystal face (the app icon).
        try {
          const img = await loadPlainImage(DEFAULT_FACE_IMAGE);
          if (cancelled) return;
          field = sampleImageField(img, cfgRef.current);
        } catch {
          field = parametricField(cfgRef.current.sampleW);
        }
      }
      if (cancelled) return;
      fieldRef.current = field;
      const density = cfgRef.current.regionDensity;
      // Small avatars cover a tiny area → keep more particles (cheap) so the face
      // reads at thumbnail size. Large avatars keep their tuned budget unchanged.
      const budget = size <= 120 ? Math.min(1, budgetRef.current * 1.6) : budgetRef.current;
      const parts = field.particles
        .filter((p) => p.rng < densityKeep(p.region, density) * budget)
        .map((p) => ({ ...p }))
        .sort((a, b) => depthKey(a) - depthKey(b)); // back-to-front layering
      renderRef.current = parts;
      // Precompute numeric colors + allocate GPU attribute arrays for this set.
      const fallback = parseRgb(cfgRef.current.haloColor) ?? [255, 200, 140];
      const cols = new Float32Array(parts.length * 3);
      for (let i = 0; i < parts.length; i++) {
        const rgb = parseRgb(parts[i]!.color) ?? fallback;
        cols[i * 3] = rgb[0] / 255; cols[i * 3 + 1] = rgb[1] / 255; cols[i * 3 + 2] = rgb[2] / 255;
      }
      colorsRef.current = cols;
      glArraysRef.current = {
        pos: new Float32Array(parts.length * 2),
        size: new Float32Array(parts.length),
        col: new Float32Array(parts.length * 4),
      };
      formingRef.current = 0;
    }
    void build();
    return () => { cancelled = true; };
  }, [buildKey, resolvedPhoto]);

  // Animation loop — runs once, reads live config + mode via refs.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    // Small avatars (e.g. the mobile chat header) render at full retina (3×) so
    // the face stays crisp; large ones cap at 2× to spare the GPU.
    const dpr = Math.min(window.devicePixelRatio || 1, size <= 120 ? 3 : 2);

    // Renderer: GPU (WebGL) if the user opted in and it initializes, else the
    // proven 2D canvas. The canvas is keyed by renderer so switching mounts a
    // fresh element (a canvas can't change context type once chosen).
    let glR: GLRenderer | null = null;
    if (renderer === 'webgl') {
      glR = createGLRenderer(canvas);
      if (glR) glR.resize(size, size, dpr);
    }
    let ctx: CanvasRenderingContext2D | null = null;
    if (!glR) {
      canvas.width = size * dpr; canvas.height = size * dpr;
      canvas.style.width = size + 'px'; canvas.style.height = size + 'px';
      ctx = canvas.getContext('2d')!;
      ctx.scale(dpr, dpr);
    }

    // Accessibility: honor the OS "reduce motion" setting by damping all idle
    // motion to a calm, near-static cloud.
    const reducedMq = typeof window.matchMedia === 'function'
      ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;
    let reduced = reducedMq?.matches ?? false;
    const onReduced = () => { reduced = reducedMq?.matches ?? false; };
    reducedMq?.addEventListener?.('change', onReduced);

    const t0 = performance.now();
    let last = t0;
    let sT = 0;
    let smoothed: MotionParams | null = null;
    let raf = 0;
    // Eye-blink scheduler + slow breathing phase (shared across frames).
    let blinkPhase = 0;
    let nextBlink = 1.5 + Math.random() * 3;
    // Adaptive quality — trims background dots if the frame budget slips, so the
    // loop holds 60fps while still rendering everything it can. Culling is
    // spatially UNIFORM (never a contiguous slice) and never touches features.
    let frameAvg = 16;
    let quality = 1;
    let frameCount = 0;
    const supportsBloom = !reduced;

    // Performance: pause the render loop entirely while the tab is hidden.
    let hidden = typeof document !== 'undefined' && document.hidden;
    const onVis = () => {
      const nowHidden = document.hidden;
      if (nowHidden === hidden) return;
      hidden = nowHidden;
      if (!hidden && !raf) { last = performance.now(); raf = requestAnimationFrame(tick); }
    };
    document.addEventListener('visibilitychange', onVis);

    function tick(now: number) {
      if (hidden) { raf = 0; return; } // stopped; visibilitychange will resume
      const particles = renderRef.current;
      const cfgNow = cfgRef.current;
      const dt = Math.min(50, now - last) / 1000;
      last = now;
      const T = (now - t0) / 1000;
      const st = modeRef.current;
      const stageW = size, stageH = size;

      if (ctx) ctx.clearRect(0, 0, stageW, stageH);

      if (!particles || !fieldRef.current) { raf = requestAnimationFrame(tick); return; }
      const glArr = glArraysRef.current;
      const glCols = colorsRef.current;
      const imgAspect = fieldRef.current.aspect;

      const margin = 0.06;
      let boxW = stageW * (1 - margin * 2);
      let boxH = boxW / imgAspect;
      if (boxH > stageH * (1 - margin * 2)) { boxH = stageH * (1 - margin * 2); boxW = boxH * imgAspect; }
      const boxX = (stageW - boxW) / 2, boxY = (stageH - boxH) / 2;

      formingRef.current = Math.min(1.4, formingRef.current + dt / 3.0);

      // Resolve the avatar stage backdrop. 'auto' contrasts the page: a strong
      // dark orb on light themes (so the glowing dots pop) and a faint dark
      // vignette on dark themes. An explicit color is used as-is. The dot render
      // mode follows the STAGE luminance, not the page.
      const pageLight = document.body.classList.contains('theme-light');
      const stageBg = cfgNow.stageBg || 'auto';
      let stageRgb: [number, number, number] = [8, 8, 11];
      let stageAlpha = pageLight ? 0.96 : 0.42;
      let lightStage = false;
      if (stageBg === 'transparent' || stageBg === 'none') {
        stageAlpha = 0; lightStage = pageLight;
      } else if (stageBg !== 'auto') {
        const m = /^#?([0-9a-f]{6})$/i.exec(stageBg.trim());
        if (m) {
          const n = parseInt(m[1]!, 16);
          stageRgb = [(n >> 16) & 255, (n >> 8) & 255, n & 255];
          stageAlpha = 0.96;
          lightStage = (0.299 * stageRgb[0] + 0.587 * stageRgb[1] + 0.114 * stageRgb[2]) / 255 > 0.55;
        }
      }
      if (ctx && stageAlpha > 0) {
        ctx.globalCompositeOperation = 'source-over';
        const [sr, sg, sb] = stageRgb;
        const g = ctx.createRadialGradient(stageW / 2, stageH * 0.45, 0, stageW / 2, stageH * 0.45, stageW * 0.62);
        g.addColorStop(0, `rgba(${sr},${sg},${sb},${stageAlpha})`);
        g.addColorStop(1, `rgba(${sr},${sg},${sb},0)`);
        ctx.fillStyle = g;
        ctx.fillRect(0, 0, stageW, stageH);
      }
      // Dots: dark ink on a light stage, glowing additive on a dark stage.
      const light = lightStage;
      if (ctx) ctx.globalCompositeOperation = light ? 'source-over' : 'lighter';
      let glN = 0; // points written to the GPU buffers this frame

      const target = motionParams(st, T, cfgNow);
      if (!smoothed) smoothed = { ...target };
      const ease = 1 - Math.exp(-dt * 1.6);
      for (const k of ['driftAmp', 'listen', 'think', 'speak', 'focusMul', 'timeMul'] as const) {
        smoothed[k] += (target[k] - smoothed[k]) * ease;
      }
      smoothed.alphaMul = target.alphaMul;
      const params = smoothed;
      sT += dt * params.timeMul;

      const fid = cfgNow.fidelity;
      const fidDrift = 1.6 - fid * 1.45;
      const fidFocusBoost = fid * 0.65;
      const fidSize = 0.75 + fid * 0.55;
      const fidAlpha = 0.85 + fid * 0.35;

      // Expression layer (smile / eyes / brow) on top of the saved config.
      const ex = EXPRESSIONS[exprRef.current] ?? EXPRESSIONS.neutral;
      const smile = Math.max(-1, Math.min(1, cfgNow.smile + ex.smile));
      const browLift = ex.brow;

      // Ambient idle motion is damped under prefers-reduce-motion.
      const reduceMul = reduced ? 0.12 : 1;

      // Eye blink — a quick triangular close/open at random intervals (off when
      // reduce-motion is requested).
      blinkPhase += dt;
      let blink = 0;
      if (!reduced) {
        if (blinkPhase > nextBlink) {
          const into = blinkPhase - nextBlink;
          const dur = 0.18;
          if (into < dur) { const x = into / dur; blink = 1 - Math.abs(x - 0.5) * 2; }
          else { blinkPhase = 0; nextBlink = 2.6 + Math.random() * 4; }
        }
      }
      const eyeOpen = Math.max(0, Math.min(1, cfgNow.eyeOpen + ex.eyeOpen)) * (1 - blink * 0.92);
      const teethVis = cfgNow.teethVis;
      const dotSize = cfgNow.dotSize;

      // Lip-sync amplitude (0..1) from whatever voice is speaking; plus subtle
      // breathing + head sway applied to the whole cloud.
      const mouthLvl = st === 'speaking' ? speechLevel() : 0;
      const breathY = Math.sin(sT * 0.85) * boxH * 0.012 * reduceMul;
      const swayX = Math.sin(sT * 0.33 + 0.6) * boxW * 0.013 * reduceMul;

      // Adaptive quality: track frame time, gently reduce only after a warm-up,
      // and never below 0.72 (so culling is barely perceptible).
      frameAvg = frameAvg * 0.9 + Math.min(60, dt * 1000) * 0.1;
      frameCount++;
      if (frameCount > 40) {
        if (frameAvg > 30 && quality > 0.72) quality -= 0.015;
        else if (frameAvg < 18 && quality < 1) quality = Math.min(1, quality + 0.01);
      }

      for (let i = 0; i < particles.length; i++) {
        const p = particles[i]!;
        // Uniform, position-independent culling under load — features are kept.
        // p.phase is a uniform random in [0, 2π); 0.159155 ≈ 1/(2π) maps it to
        // [0,1), so this drops a spatially-even fraction of background dots.
        if (quality < 1 && p.featureWeight < 0.35 && p.phase * 0.159155 > quality) continue;
        const localForm = Math.max(0, Math.min(1, formingRef.current - p.formDelay));
        const form = easeOutCubic(localForm);

        const targetX = boxX + p.tx * boxW;
        const targetY = boxY + p.ty * boxH;

        const drift = params.driftAmp * p.dr * fidDrift * reduceMul;
        const driftX = Math.sin(sT * p.speed + p.phase) * drift;
        const driftY = Math.cos(sT * p.speed * 0.9 + p.phase * 1.3) * drift;

        let listenX = 0, listenY = 0;
        if (params.listen > 0) {
          const dx = targetX - stageW / 2, dy = targetY - stageH / 2;
          const d = Math.sqrt(dx * dx + dy * dy) || 1;
          const wave = Math.sin(sT * 2.4 - d * 0.025) * 0.5 + 0.5;
          const amt = params.listen * 8 * wave;
          listenX = (dx / d) * amt; listenY = (dy / d) * amt;
        }
        let thinkX = 0, thinkY = 0;
        if (params.think > 0) {
          thinkX = Math.sin(sT * 5 + p.phase * 3.7) * params.think * 6;
          thinkY = Math.cos(sT * 4.3 + p.phase * 2.4) * params.think * 6;
        }
        let speakY = 0, speakX = 0;
        if (params.speak > 0 && p.mouthWeight > 0) {
          // Real lip-sync: the jaw opens proportionally to the live audio level,
          // with a little high-frequency texture so it never looks frozen.
          const tex = Math.sin(sT * 9.5 + p.phase * 0.2) * 0.5 + 0.5;
          const amt = params.speak * p.mouthWeight * (mouthLvl * 16 + tex * 2.0);
          speakY = amt * 0.85 * (p.ty > 0.6 ? 1 : 0.55); // lower lip drops more
          speakX = (p.tx - 0.5) * amt * 0.4;
        }

        let deformY = 0;
        if (smile !== 0 && p.region === 'mouth') {
          const dx = p.tx - 0.51;
          deformY += -smile * (dx * dx) * boxH * 5.0;
        }
        if (eyeOpen < 1 && (p.region === 'eyes' || p.region === 'brows')) {
          const dy = p.ty - 0.43;
          deformY += -dy * boxH * (1 - eyeOpen) * (p.region === 'eyes' ? 1 : 0.3);
        }
        if (browLift !== 0 && p.region === 'brows') {
          deformY += -browLift * boxH * 0.03; // raise (+) / furrow (−)
        }

        // Depth parallax: foreground features sway a touch more than the cloud.
        const par = swayX * (0.6 + p.featureWeight * 0.9);
        const fx = targetX + driftX + listenX + thinkX + speakX + par;
        const fy = targetY + driftY + listenY + thinkY + speakY + deformY + breathY;

        if (form < 1) {
          const ox = p.ox * stageW, oy = p.oy * stageH;
          p.cx = ox + (fx - ox) * form; p.cy = oy + (fy - oy) * form;
        } else {
          p.cx += (fx - p.cx) * 0.18; p.cy += (fy - p.cy) * 0.18;
        }

        const depth = 0.25 + 0.75 * form;
        const effectiveFocus = params.focusMul + fidFocusBoost;
        const focusBoost = Math.min(1.6, 1 + p.featureWeight * form * effectiveFocus * 0.45);
        const baseAlpha = (0.10 + p.lum * 0.85) * depth * fidAlpha;
        const flicker = 0.85 + Math.sin(sT * 3 + p.phase) * 0.15 * (p.halo ? 0 : 1);
        const teethMul = p.isTeeth ? teethVis : 1;
        const alpha = baseAlpha * flicker * params.alphaMul * focusBoost * teethMul;

        const size2 = (0.7 + p.lum * 1.6 + (params.think + params.speak * p.mouthWeight) * 0.6)
          * (0.45 + 0.55 * form) * focusBoost * fidSize * dotSize;

        const color = p.color || cfgNow.haloColor;
        const aFinal = Math.max(0, Math.min(1, light ? alpha * 1.7 : alpha));

        if (glR && glArr && glCols) {
          // GPU path: accumulate this point into the attribute buffers.
          glArr.pos[glN * 2] = p.cx; glArr.pos[glN * 2 + 1] = p.cy;
          glArr.size[glN] = Math.max(1, size2 * 2); // diameter
          if (light) { glArr.col[glN * 4] = 46 / 255; glArr.col[glN * 4 + 1] = 40 / 255; glArr.col[glN * 4 + 2] = 32 / 255; }
          else { glArr.col[glN * 4] = glCols[i * 3]!; glArr.col[glN * 4 + 1] = glCols[i * 3 + 1]!; glArr.col[glN * 4 + 2] = glCols[i * 3 + 2]!; }
          glArr.col[glN * 4 + 3] = aFinal;
          glN++;
        } else if (ctx) {
          ctx.globalAlpha = aFinal;
          ctx.fillStyle = light ? 'rgb(46,40,32)' : color;
          // Soft bloom on the bright facial features (dark stage only) — the
          // luminous "crystal" look without blurring the whole cloud.
          if (supportsBloom && !light && p.featureWeight > 0.85) {
            ctx.shadowBlur = 3 * dotSize; ctx.shadowColor = color;
          } else {
            ctx.shadowBlur = 0;
          }
          ctx.beginPath();
          ctx.arc(p.cx, p.cy, Math.max(0.3, size2), 0, Math.PI * 2);
          ctx.fill();
        }
      }

      if (glR && glArr) {
        glR.frame({
          bg: stageAlpha > 0 ? { r: stageRgb[0] / 255, g: stageRgb[1] / 255, b: stageRgb[2] / 255, a: stageAlpha } : null,
          additive: !light,
          count: glN,
          positions: glArr.pos, sizes: glArr.size, colors: glArr.col,
        });
      } else if (ctx) {
        ctx.shadowBlur = 0;
        ctx.globalAlpha = 1;
        ctx.globalCompositeOperation = 'source-over';
      }
      raf = requestAnimationFrame(tick);
    }
    raf = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(raf);
      document.removeEventListener('visibilitychange', onVis);
      reducedMq?.removeEventListener?.('change', onReduced);
      glR?.dispose();
    };
  }, [size, renderer]);

  return (
    <canvas
      key={`avatar-${renderer}`}
      ref={canvasRef}
      role="img"
      aria-label={`Digital twin avatar — ${mode}`}
      style={{ width: size, height: size, display: 'block' }}
    />
  );
}
